import { useContext, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

async function fetchJournalCalendar(month, accountId, paperTrading, token) {
  const url = new URL(`${BACKEND_URL}/performance/journal-calendar`);
  url.searchParams.set('month', month);
  if (accountId) url.searchParams.set('accountId', String(accountId));
  if (paperTrading !== undefined && paperTrading !== null) {
    url.searchParams.set('paperTrading', String(paperTrading));
  }
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`journal-calendar ${res.status}`);
  return res.json();
}

/**
 * Fetches per-day journal status for a given month.
 * Returns { days: [{ date, tradeCount, journaledCount }] }
 *
 * Invalidates when a journal is created or updated.
 */
export function useJournalCalendar({ month, accountId, paperTrading } = {}) {
  const { user } = useContext(AuthContext);
  const token = user?.token;
  const queryClient = useQueryClient();

  const queryKey = ['journalCalendar', month, accountId, paperTrading];

  const query = useQuery({
    queryKey,
    queryFn: () => fetchJournalCalendar(month, accountId, paperTrading, token),
    enabled: !!token && !!month,
    staleTime: 30000,
  });

  useEffect(() => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ['journalCalendar'] });
    };
    window.addEventListener('tradeJournal:created', invalidate);
    window.addEventListener('tradeJournal:updated', invalidate);
    return () => {
      window.removeEventListener('tradeJournal:created', invalidate);
      window.removeEventListener('tradeJournal:updated', invalidate);
    };
  }, [queryClient]);

  return {
    days: query.data?.days || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
